import React, { useMemo } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";
import { DatePicker } from "components/shared/form/Datepicker";
import clsx from "clsx";
import dayjs from "dayjs";

const FILE_TYPE_OPTIONS = [
  { value: "pdf", label: "PDF (.pdf)" },
  { value: "doc", label: "Word (.doc)" },
  { value: "docx", label: "Word (.docx)" },
  { value: "jpg", label: "JPEG (.jpg)" },
  { value: "jpeg", label: "JPEG (.jpeg)" },
  { value: "png", label: "PNG (.png)" },
  { value: "xls", label: "Excel (.xls)" },
  { value: "xlsx", label: "Excel (.xlsx)" },
  { value: "csv", label: "CSV (.csv)" },
];

/**
 * Compact DocumentsFilter component used across Leads, Jobs, and Global Settings.
 * Uses MultiSelectCheckboxDropdown for File Type filter and DatePicker for date range filters.
 */
const DocumentsFilter = ({
  filterState = {},
  onFilterChange = () => { },
  onClearFilters = () => { },
  className = "",
}) => {
  const rawFileType = filterState?.fileType ?? "";
  const startDate = filterState?.startDate ?? "";
  const endDate = filterState?.endDate ?? "";
  const modifiedStartDate = filterState?.modifiedStartDate ?? "";
  const modifiedEndDate = filterState?.modifiedEndDate ?? "";

  // Normalize fileType string without leading dots for display and dropdown match
  const normalizedFileType = useMemo(() => {
    if (Array.isArray(rawFileType)) {
      return rawFileType.map((s) => String(s).replace(/^\./, "")).join(",");
    }
    return String(rawFileType || "").replace(/^\./, "");
  }, [rawFileType]);

  // Selected file types array for MultiSelectCheckboxDropdown
  const selectedFileTypes = useMemo(() => {
    if (!normalizedFileType) return [];
    return normalizedFileType.split(",").map((s) => s.trim()).filter(Boolean);
  }, [normalizedFileType]);

  // Uploaded date range value string for DatePicker
  const uploadedDateValue = useMemo(() => {
    if (startDate && endDate) {
      return `${dayjs(startDate).format("DD-MM-YYYY")} to ${dayjs(endDate).format("DD-MM-YYYY")}`;
    } else if (startDate) {
      return dayjs(startDate).format("DD-MM-YYYY");
    }
    return "";
  }, [startDate, endDate]);

  // Modified date range value string for DatePicker
  const modifiedDateValue = useMemo(() => {
    if (modifiedStartDate && modifiedEndDate) {
      return `${dayjs(modifiedStartDate).format("DD-MM-YYYY")} to ${dayjs(modifiedEndDate).format("DD-MM-YYYY")}`;
    } else if (modifiedStartDate) {
      return dayjs(modifiedStartDate).format("DD-MM-YYYY");
    }
    return "";
  }, [modifiedStartDate, modifiedEndDate]);


  // Handle uploaded date range selection from DatePicker
  const handleUploadedDateChange = (selectedDates) => {
    if (selectedDates && selectedDates.length === 2) {
      onFilterChange({
        startDate: dayjs(selectedDates[0]).format("YYYY-MM-DD"),
        endDate: dayjs(selectedDates[1]).format("YYYY-MM-DD"),
      });
    } else if (selectedDates && selectedDates.length === 1) {
      onFilterChange({
        startDate: dayjs(selectedDates[0]).format("YYYY-MM-DD"),
        endDate: null,
      });
    } else {
      onFilterChange({
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleUploadedDateClose = (selectedDates) => {
    if (selectedDates && selectedDates.length === 1) {
      const formattedDate = dayjs(selectedDates[0]).format("YYYY-MM-DD");
      onFilterChange({
        startDate: formattedDate,
        endDate: formattedDate,
      });
    } else if (startDate && !endDate) {
      onFilterChange({
        startDate: startDate,
        endDate: startDate,
      });
    }
  };

  // Handle modified date range selection from DatePicker
  const handleModifiedDateChange = (selectedDates) => {
    if (selectedDates && selectedDates.length === 2) {
      onFilterChange({
        modifiedStartDate: dayjs(selectedDates[0]).format("YYYY-MM-DD"),
        modifiedEndDate: dayjs(selectedDates[1]).format("YYYY-MM-DD"),
      });
    } else if (selectedDates && selectedDates.length === 1) {
      onFilterChange({
        modifiedStartDate: dayjs(selectedDates[0]).format("YYYY-MM-DD"),
        modifiedEndDate: null,
      });
    } else {
      onFilterChange({
        modifiedStartDate: null,
        modifiedEndDate: null,
      });
    }
  };

  const handleModifiedDateClose = (selectedDates) => {
    if (selectedDates && selectedDates.length === 1) {
      const formattedDate = dayjs(selectedDates[0]).format("YYYY-MM-DD");
      onFilterChange({
        modifiedStartDate: formattedDate,
        modifiedEndDate: formattedDate,
      });
    } else if (modifiedStartDate && !modifiedEndDate) {
      onFilterChange({
        modifiedStartDate: modifiedStartDate,
        modifiedEndDate: modifiedStartDate,
      });
    }
  };

  return (
    <div className={clsx("flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-none pb-0.5", className)}>
      {/* Hide "Filter by:" label when container is non-expanded (<620px) */}
      <div className="hidden @[620px]:flex items-center gap-1 text-gray-500 shrink-0">
        <label className="shrink-0 text-xs sm:text-sm font-medium">Filter by:</label>
      </div>

      {/* Multi-Select File Type Filter */}
      <div className="flex-1 min-w-[110px] @[620px]:flex-initial @[620px]:w-44">
        <MultiSelectCheckboxDropdown
          options={FILE_TYPE_OPTIONS}
          value={selectedFileTypes}
          onChange={(newVal) => {
            const cleanVal = Array.isArray(newVal) ? newVal.join(",") : newVal;
            onFilterChange("fileType", cleanVal || null);
          }}
          placeholder="All File Types"
          searchPlaceholder="Search file types..."
          isMulti={false}
          size="sm"
          className="w-full"
        />
      </div>

      {/* Uploaded Date Range Filter */}
      <div className="relative flex-1 min-w-[220px] @[620px]:flex-initial @[620px]:w-64">
        <DatePicker
          options={{
            mode: "range",
            dateFormat: "d-m-Y",
            onClose: handleUploadedDateClose,
          }}
          value={uploadedDateValue}
          onChange={handleUploadedDateChange}
          placeholder="Uploaded date range"
          className="w-full text-xs pr-7"
        />
        {uploadedDateValue && (
          <button
            type="button"
            onClick={() => handleUploadedDateChange([])}
            className="absolute top-1/2 right-2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer z-10"
            title="Clear uploaded date filter"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Modified Date Range Filter */}
      <div className="relative flex-1 min-w-[220px] @[620px]:flex-initial @[620px]:w-64">
        <DatePicker
          options={{
            mode: "range",
            dateFormat: "d-m-Y",
            onClose: handleModifiedDateClose,
          }}
          value={modifiedDateValue}
          onChange={handleModifiedDateChange}
          placeholder="Modified date range"
          className="w-full text-xs pr-7"
        />
        {modifiedDateValue && (
          <button
            type="button"
            onClick={() => handleModifiedDateChange([])}
            className="absolute top-1/2 right-2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer z-10"
            title="Clear modified date filter"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentsFilter;
